import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Plus, Trash2, Edit2, Save, X, Award } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../App';
import { BadgePill } from '../components/BadgeDisplay';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const COLORS = ['cyan', 'amber', 'green', 'purple', 'blue', 'rose', 'orange', 'slate'];
const ICON_SUGGESTIONS = [
  'award', 'shield-check', 'heart', 'star', 'trophy', 'crown', 'medal',
  'users', 'map-pin', 'camera', 'book-open', 'accessibility', 'dog', 'baby',
  'gem', 'landmark', 'zap', 'sparkles', 'flame', 'globe',
];

const BadgeForm = ({ badge, onSave, onCancel }) => {
  const [form, setForm] = useState({
    name: badge?.name || '',
    icon: badge?.icon || 'award',
    color: badge?.color || 'cyan',
    description: badge?.description || '',
  });
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return toast.error('Name is required');
    setSaving(true);
    await onSave(form);
    setSaving(false);
  };

  return (
    <form onSubmit={submit} className="bg-white rounded-xl border-2 border-cyan-200 p-6 mb-6" data-testid="badge-form">
      <h3 className="text-lg font-bold text-gray-900 mb-4">
        {badge ? 'Edit Badge' : 'New Badge'}
      </h3>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-1">Name</label>
          <input
            type="text"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            placeholder="Official Tour"
            maxLength={60}
            data-testid="badge-name-input"
            className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:border-cyan-500 focus:ring-2 focus:ring-cyan-200"
          />
        </div>
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-1">Color</label>
          <div className="flex flex-wrap gap-2">
            {COLORS.map((c) => (
              <button
                type="button"
                key={c}
                onClick={() => setForm({ ...form, color: c })}
                data-testid={`badge-color-${c}`}
                className={`w-8 h-8 rounded-full border-2 ${form.color === c ? 'ring-2 ring-offset-2 ring-cyan-500' : ''} bg-${c}-400`}
                style={{ backgroundColor: `var(--color-${c}, #06b6d4)` }}
                title={c}
              >
                {/* Tailwind JIT needs explicit classes; fallback color swatch below */}
                <span className={`block w-full h-full rounded-full ${c === 'slate' ? 'bg-slate-400' : `bg-${c}-400`}`} />
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="mb-4">
        <label className="block text-sm font-semibold text-gray-700 mb-1">Icon (lucide name, e.g. "award", "shield-check")</label>
        <input
          type="text"
          value={form.icon}
          onChange={(e) => setForm({ ...form, icon: e.target.value })}
          placeholder="award"
          data-testid="badge-icon-input"
          className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:border-cyan-500 focus:ring-2 focus:ring-cyan-200"
        />
        <div className="flex flex-wrap gap-2 mt-2">
          {ICON_SUGGESTIONS.map((i) => (
            <button
              type="button"
              key={i}
              onClick={() => setForm({ ...form, icon: i })}
              className={`text-xs px-2 py-1 rounded-full border ${form.icon === i ? 'bg-cyan-500 text-white border-cyan-500' : 'bg-white text-gray-700 border-gray-300 hover:border-cyan-400'}`}
            >
              {i}
            </button>
          ))}
        </div>
      </div>

      <div className="mb-4">
        <label className="block text-sm font-semibold text-gray-700 mb-1">Description (optional tooltip)</label>
        <input
          type="text"
          value={form.description}
          onChange={(e) => setForm({ ...form, description: e.target.value })}
          placeholder="Shown as tooltip on hover"
          maxLength={200}
          data-testid="badge-desc-input"
          className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:border-cyan-500 focus:ring-2 focus:ring-cyan-200"
        />
      </div>

      {/* Preview */}
      <div className="mb-4">
        <label className="block text-sm font-semibold text-gray-700 mb-2">Preview</label>
        <BadgePill badge={form} />
      </div>

      <div className="flex gap-3">
        <button
          type="submit"
          disabled={saving}
          data-testid="save-badge-btn"
          className="flex-1 bg-cyan-500 hover:bg-cyan-600 text-white py-2 rounded-lg font-bold transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
        >
          <Save className="w-4 h-4" />
          {saving ? 'Saving…' : 'Save Badge'}
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="px-6 py-2 rounded-lg border-2 border-gray-300 text-gray-700 font-bold hover:bg-gray-100 transition-colors flex items-center gap-2"
        >
          <X className="w-4 h-4" />
          Cancel
        </button>
      </div>
    </form>
  );
};

const AdminBadgesPage = () => {
  const navigate = useNavigate();
  const { token } = useAuth();
  const [badges, setBadges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);

  const fetchBadges = async () => {
    try {
      const { data } = await axios.get(`${API}/admin/badges`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setBadges(data);
    } catch {
      toast.error('Failed to load badges');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBadges();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSave = async (form) => {
    try {
      if (editing) {
        await axios.put(`${API}/admin/badges/${editing.badge_id}`, form, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.success('Badge updated');
      } else {
        await axios.post(`${API}/admin/badges`, form, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.success('Badge created');
      }
      setShowForm(false);
      setEditing(null);
      fetchBadges();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    }
  };

  const handleDelete = async (badge) => {
    if (!window.confirm(`Remove badge "${badge.name}"? It will be unassigned from all tours.`)) return;
    try {
      await axios.delete(`${API}/admin/badges/${badge.badge_id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Badge removed');
      fetchBadges();
    } catch {
      toast.error('Delete failed');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-cyan-500 border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 pb-24">
      <header className="bg-cyan-500 text-white py-6 px-4">
        <div className="max-w-4xl mx-auto">
          <button
            onClick={() => navigate('/admin')}
            className="flex items-center gap-2 text-white/80 hover:text-white mb-4 transition-colors"
            data-testid="back-to-admin-btn"
          >
            <ArrowLeft className="w-5 h-5" />
            Back to Dashboard
          </button>
          <div className="flex items-center justify-between">
            <h1 className="text-3xl font-black uppercase">Credibility Badges</h1>
            {!showForm && (
              <button
                onClick={() => setShowForm(true)}
                data-testid="add-badge-btn"
                className="bg-white text-cyan-600 px-4 py-2 rounded-full font-bold hover:bg-cyan-50 transition-colors flex items-center gap-2"
              >
                <Plus className="w-5 h-5" />
                Add Badge
              </button>
            )}
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-8">
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-6">
          <p className="text-blue-800 text-sm">
            <strong>Badges</strong> appear on tour detail pages to signal credibility (e.g.
            <em> Official Tour, Family Friendly, Top Rated</em>). Assign them per-tour from the tour edit page.
          </p>
        </div>

        {showForm && (
          <BadgeForm
            badge={editing}
            onSave={handleSave}
            onCancel={() => { setShowForm(false); setEditing(null); }}
          />
        )}

        {badges.length === 0 ? (
          <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
            <Award className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-xl font-bold text-gray-900 mb-2">No badges yet</h3>
            <p className="text-gray-600 mb-4">Create your first credibility badge to attach to tours.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {badges.map((b) => (
              <div
                key={b.badge_id}
                className="bg-white rounded-xl border border-gray-200 p-4 flex items-center justify-between gap-4"
                data-testid={`badge-row-${b.badge_id}`}
              >
                <div className="flex items-center gap-4 flex-1 min-w-0">
                  <BadgePill badge={b} />
                  <div className="min-w-0">
                    <p className="font-bold text-gray-900 truncate">{b.name}</p>
                    {b.description && (
                      <p className="text-sm text-gray-500 truncate">{b.description}</p>
                    )}
                  </div>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => { setEditing(b); setShowForm(true); }}
                    data-testid={`edit-badge-${b.badge_id}`}
                    className="p-2 text-gray-500 hover:text-cyan-600 hover:bg-cyan-50 rounded-lg transition-all"
                  >
                    <Edit2 className="w-5 h-5" />
                  </button>
                  <button
                    onClick={() => handleDelete(b)}
                    data-testid={`delete-badge-${b.badge_id}`}
                    className="p-2 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition-all"
                  >
                    <Trash2 className="w-5 h-5" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
};

export default AdminBadgesPage;
